import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { auth, storage, db } from '../services/firebase';
import { ref, uploadBytes, getDownloadURL, ref as storageRef, deleteObject } from 'firebase/storage';
import { updateProfile, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';

const AccountSettingsPage = () => {
  const { currentUser, userProfile, refreshUserProfile } = useAuth();
  const [photo, setPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  // Profile picture upload handler
  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    setUploadSuccess('');
    try {
      const storageRef = ref(storage, `profile_pictures/${currentUser.uid}/${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      // Update Auth profile
      await updateProfile(currentUser, { photoURL: url });
      // Update Firestore profile (create if not exists)
      const userType = userProfile?.userType || 'parents';
      const userDoc = doc(db, userType, currentUser.uid);
      await setDoc(userDoc, { profile: { photoURL: url } }, { merge: true });
      if (refreshUserProfile) await refreshUserProfile();
      setUploadSuccess('Profile picture updated!');
    } catch (err) {
      setUploadError('Failed to upload photo: ' + (err.message || err));
    } finally {
      setUploading(false);
    }
  };

  // Password change handler
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');
    if (newPassword !== confirmPassword) {
      setPwError('New passwords do not match.');
      return;
    }
    setPwLoading(true);
    try {
      // Re-authenticate
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
      setPwSuccess('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwError('Failed to update password: ' + (err.message || err));
    } finally {
      setPwLoading(false);
    }
  };

  const handleRemovePhoto = async () => {
    setUploading(true);
    setUploadError('');
    setUploadSuccess('');
    try {
      // Delete from storage (optional, only if you want to clean up)
      const photoURL = userProfile?.profile?.photoURL || currentUser.photoURL;
      if (photoURL) {
        // Try to extract the storage path from the URL
        const pathMatch = photoURL.match(/profile_pictures%2F(.+)$/);
        if (pathMatch) {
          const storagePath = decodeURIComponent('profile_pictures/' + pathMatch[1].split('?')[0]);
          const fileRef = storageRef(storage, storagePath);
          await deleteObject(fileRef).catch(() => {}); // Ignore if not found
        }
      }
      // Remove from Auth
      await updateProfile(currentUser, { photoURL: null });
      await currentUser.reload();
      // Remove from Firestore
      const userType = userProfile?.userType || 'parents';
      const userDoc = doc(db, userType, currentUser.uid);
      await setDoc(userDoc, { profile: { photoURL: null } }, { merge: true });
      if (refreshUserProfile) await refreshUserProfile();
      setUploadSuccess('Profile picture removed!');
    } catch (err) {
      setUploadError('Failed to remove photo: ' + (err.message || err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="account-settings-page">
      <h1>Account Settings</h1>
      <div className="settings-section">
        <h2>Change Profile Picture</h2>
        <input type="file" accept="image/*" onChange={handlePhotoChange} disabled={uploading} />
        {uploading && <p>Uploading...</p>}
        {uploadError && <p className="error-message">{uploadError}</p>}
        {uploadSuccess && <p className="success-message">{uploadSuccess}</p>}
        {currentUser.photoURL || userProfile?.profile?.photoURL ? (
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <img
              src={currentUser.photoURL || userProfile?.profile?.photoURL}
              alt="Profile"
              style={{ width: 100, height: 100, borderRadius: '50%', marginBottom: 20 }}
            />
            <button
              className="remove-photo-btn"
              onClick={handleRemovePhoto}
              disabled={uploading}
              style={{
                marginTop: 16,
                background: 'linear-gradient(90deg, #e53935 0%, #d32f2f 100%)',
                color: '#fff',
                border: '2px solid #b71c1c',
                borderRadius: 24,
                padding: '0 32px',
                height: 48,
                minWidth: 180,
                fontWeight: 800,
                fontSize: '1.15rem',
                letterSpacing: '0.7px',
                cursor: 'pointer',
                transition: 'background 0.2s, color 0.2s, box-shadow 0.2s, transform 0.15s',
                boxShadow: '0 4px 16px rgba(244,67,54,0.12)',
                outline: 'none',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'auto',
                textShadow: '0 1px 2px rgba(0,0,0,0.08)'
              }}
              onMouseOver={e => {
                e.currentTarget.style.background = 'linear-gradient(90deg, #b71c1c 0%, #c62828 100%)';
                e.currentTarget.style.borderColor = '#c62828';
                e.currentTarget.style.transform = 'scale(1.04)';
                e.currentTarget.style.boxShadow = '0 6px 24px rgba(244,67,54,0.18)';
              }}
              onMouseOut={e => {
                e.currentTarget.style.background = 'linear-gradient(90deg, #e53935 0%, #d32f2f 100%)';
                e.currentTarget.style.borderColor = '#b71c1c';
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(244,67,54,0.12)';
              }}
            >
              Remove Photo
            </button>
          </div>
        ) : null}
      </div>
      <div className="settings-section">
        <h2>Change Password</h2>
        <form onSubmit={handlePasswordChange}>
          <div className="form-group">
            <label>Current Password</label>
            <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>New Password</label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Confirm New Password</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
          </div>
          <button type="submit" disabled={pwLoading}>{pwLoading ? 'Updating...' : 'Change Password'}</button>
          {pwError && <p className="error-message">{pwError}</p>}
          {pwSuccess && <p className="success-message">{pwSuccess}</p>}
        </form>
      </div>
    </div>
  );
};

export default AccountSettingsPage; 